import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Hash, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import BrandCard from '../components/BrandCard';
import FashionCard from '../components/FashionCard';
import ChallengeCard from '../components/ChallengeCard';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1571924848943-25c2c95bbb4b?w=1920&q=80";
const BOOK_IMAGE = "/api/static/uploads/ebook_cover.jpg";

const HomePage = () => {
  const [brands, setBrands] = useState([]);
  const [recentLooks, setRecentLooks] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [brandWeek, setBrandWeek] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [brandsRes, looksRes, challengesRes, bwRes] = await Promise.all([
        axios.get(`${API}/brands`),
        axios.get(`${API}/looks?limit=4`),
        axios.get(`${API}/challenges?status=active`),
        axios.get(`${API}/brand-week`)
      ]);
      setBrands(brandsRes.data);
      setRecentLooks(looksRes.data);
      setChallenges(challengesRes.data);
      if (bwRes.data.active) setBrandWeek(bwRes.data);
    } catch {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Fashion"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-[#0a0a0a]" />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-300 mb-6 animate-fade-in-up">
            A Social Network Dedicated to Fashion
          </p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif italic text-white mb-8 animate-fade-in-up stagger-1 text-glow">
            meet mr.fashion
          </h1>
          <p className="text-lg md:text-xl text-gray-300 font-light tracking-wide mb-12 animate-fade-in-up stagger-2 max-w-2xl mx-auto">
            Discover luxury fashion, connect with style enthusiasts, and share your unique fashion perspective with the world.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up stagger-3">
            <Link to="/feed" data-testid="explore-feed-btn">
              <Button className="btn-luxury btn-luxury-primary w-full sm:w-auto">
                Explore Feed
              </Button>
            </Link>
            <Link to="/auth" data-testid="join-community-btn">
              <Button className="btn-luxury btn-luxury-secondary w-full sm:w-auto">
                Join Community
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-px h-16 bg-gradient-to-b from-white/50 to-transparent" />
        </div>
      </section>

      {/* Brand Week Banner */}
      {brandWeek && (
        <section className="px-6 md:px-12 py-4 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto">
            <Link to="/one-brand-week" className="block group" data-testid="brand-week-banner">
              <div className="relative overflow-hidden border border-white/10 hover:border-white/25 transition-all duration-500 p-6 md:p-8" style={{ background: `linear-gradient(135deg, ${brandWeek.accent_color}10, #111111, ${brandWeek.accent_color}05)` }}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 rounded-full border flex items-center justify-center shrink-0" style={{ borderColor: `${brandWeek.accent_color}40`, backgroundColor: `${brandWeek.accent_color}10` }}>
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: brandWeek.accent_color }} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: brandWeek.accent_color }}>One Brand Week</p>
                      <h3 className="text-xl font-serif italic text-white">{brandWeek.brand_name}: {brandWeek.tagline}</h3>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 group-hover:text-white transition-colors" style={{ color: brandWeek.accent_color }}>
                    <span className="text-xs uppercase tracking-wider font-medium">Explore</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Fashion Icons Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] mb-4">Fashion Icons</p>
              <h2 className="text-4xl md:text-5xl font-serif italic text-white">
                Luxury Brands
              </h2>
            </div>
            <Link to="/brands" className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="view-all-brands">
              <span className="text-sm uppercase tracking-wider">View All</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Brand Showcase - Horizontal Scroll */}
          <div className="brand-showcase hide-scrollbar -mx-6 px-6 md:-mx-0 md:px-0">
            {brands.map((brand) => (
              <BrandCard key={brand.id} brand={brand} />
            ))}
          </div>
        </div>
      </section>

      {/* Recent Looks Section */}
      {recentLooks.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 bg-[#121212]">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] mb-4">Community</p>
                <h2 className="text-4xl md:text-5xl font-serif italic text-white">
                  Recent Looks
                </h2>
              </div>
              <Link to="/feed" className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="view-all-looks">
                <span className="text-sm uppercase tracking-wider">View All</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="bento-grid">
              {recentLooks.map((look, index) => (
                <FashionCard key={look.id} look={look} featured={index === 0} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Active Challenges Section */}
      {challenges.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A]">Brand Sponsored</p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-400 text-[10px] uppercase tracking-wider font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live
                  </span>
                </div>
                <h2 className="text-4xl md:text-5xl font-serif italic text-white">
                  Style Challenges
                </h2>
              </div>
              <Link to="/challenges" className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="view-all-challenges">
                <span className="text-sm uppercase tracking-wider">View All</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {challenges.slice(0, 3).map((challenge) => (
                <ChallengeCard key={challenge.id} challenge={challenge} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Campaign Promo Section */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C41E3A]/30 bg-[#C41E3A]/10 mb-8">
            <Hash className="w-4 h-4 text-[#C41E3A]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] font-medium">Active Campaign</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif italic text-white mb-6">
            #RunwayToRealLife
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Where the runway meets your wardrobe. Join the movement and share how you translate high-fashion looks into your everyday style.
          </p>
          <Link to="/campaign/runway-to-real-life" data-testid="campaign-promo-link">
            <Button className="btn-luxury btn-luxury-accent gap-2">
              Join the Campaign
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Book Promo Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Image */}
            <div className="relative group">
              <div className="aspect-[2/3] overflow-hidden bg-[#1c1c1c]">
                <img
                  src={BOOK_IMAGE}
                  alt="The Art of Luxury Fashion by Akeem Feiton"
                  className="w-full h-full object-contain img-desaturate"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -right-6 w-full h-full border border-[#C41E3A]/30 -z-10" />
            </div>

            {/* Content */}
            <div className="lg:pl-12">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-[#C41E3A]" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#C41E3A]">Featured Book</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif italic text-white mb-6 leading-tight">
                The Art of Luxury Fashion
              </h2>
              <p className="text-xl text-gray-400 font-serif italic mb-4">
                by Akeem Feiton
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                Dive deep into the world of haute couture and luxury fashion. This comprehensive guide explores 
                the history, craftsmanship, and cultural impact of the world{"'"}s most prestigious fashion houses. 
                Available now on Apple Books.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book" data-testid="learn-more-book-btn">
                  <Button className="btn-luxury btn-luxury-accent">
                    Learn More
                  </Button>
                </Link>
                <a 
                  href="https://books.apple.com/us/book/the-art-of-luxury-fashion/id6479809621" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="apple-books-link"
                >
                  <Button className="btn-luxury btn-luxury-secondary">
                    Get on Apple Books
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#121212] text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif italic text-white mb-6">
            Join the Fashion Community
          </h2>
          <p className="text-gray-400 text-lg mb-12">
            Share your style, discover inspiration, and connect with fashion enthusiasts worldwide.
          </p>
          <Link to="/auth" data-testid="cta-get-started-btn">
            <Button className="btn-luxury btn-luxury-primary">
              Get Started
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
